import React from 'react';
import { Link } from 'react-router-dom';
import usePageTitle from '../hooks/usePageTitle';
import '../styles/pages/TermsOfServicePage.css';

const TermsOfServicePage = () => {
  usePageTitle('Terms of Service');
  return (
    <div className="policy-page-container">
      <div className="return-link-container return-link-top">
        <Link to="/" className="return-link">← Return to The Icing Calculator</Link>
      </div>
      <h1>Terms of Service for The Icing Calculator</h1>
      <p>Last updated: August 19, 2026</p>

      <section>
        <h2>1. Acceptance of Terms</h2>
        <p>
          By accessing and using The Icing Calculator (the "Service"), you accept and agree to be
          bound by these Terms of Service. Any participation in this Service will constitute
          acceptance of this agreement. If you do not agree to these terms, please do not use the Service.
        </p>
      </section>

      <section>
        <h2>2. Description of Service</h2>
        <p>
          The Icing Calculator provides tools for cookie decorators to calculate royal icing quantities
          by cookie, color, and order. The Service includes:
        </p>
        <ul>
          <li><strong>Basic Calculator</strong>: free to use, no account required. Calculations
          are performed in your browser and are not saved to our servers.</li>
          <li><strong>Pro Calculator</strong>: free to use, no account required, including color
          breakdowns, order planning, and copy results to clipboard. An account is only needed to
          save Icing Plans, export results to Excel or PDF, or use certain AI-powered features.</li>
        </ul>
      </section>

      <section>
        <h2>3. User Accounts</h2>
        <p>
          An account is not required to use the Basic or Pro Calculator. Creating a free account,
          by signing in with Google or an email and password, lets you save Icing Plans, export to
          Excel or PDF, and access certain AI-powered features. You are responsible for maintaining
          the confidentiality of your account credentials and for all activities that occur under
          your account. You agree to notify us immediately at{' '}
          <a href="mailto:support@theicingcalculator.com">support@theicingcalculator.com</a> of any
          unauthorized use of your account.
        </p>
      </section>

      <section>
        <h2>4. Subscriptions and Billing</h2>
        <p>
          <em>This section describes both current and possible future terms. Paid subscription
          plans are not always active. Check the app itself for what's currently offered. Whenever
          a paid plan is active, the following terms apply to it:</em>
        </p>
        <p>
          Certain expanded features of the Pro Calculator may be offered under a paid subscription
          (monthly or annual), separate from any free trial period. If you subscribe to a paid plan,
          you agree to the following:
        </p>
        <ul>
          <li>Subscription fees are billed in advance on a recurring basis (monthly or annually,
          depending on the plan you select).</li>
          <li>Payment is processed through <strong>Stripe</strong>. We do not store your credit card
          or full payment details.</li>
          <li>Your subscription will automatically renew at the end of each billing period unless
          you cancel before the renewal date.</li>
          <li>You may cancel your subscription at any time through your Account page. Cancellation
          takes effect at the end of the current billing period; no partial refunds are issued for
          unused time.</li>
          <li>We reserve the right to change subscription pricing with reasonable advance notice.
          Continued use of the Service after a price change constitutes your acceptance of the new price.</li>
        </ul>
        <p>
          Free trials, if offered, are available to new accounts only. At the end of the trial period,
          a valid payment method would be required to continue accessing any Pro features that are
          gated behind a paid plan at that time.
        </p>
      </section>

      <section>
        <h2>5. Usage Data and Privacy</h2>
        <p>
          By using this Service, you acknowledge that we collect certain usage data to improve the
          Service and understand how features are used. This includes logging when you use specific
          actions such as copying results, exporting to Excel, or exporting to PDF. For full details
          on what data we collect and how it is used, please review our{' '}
          <Link to="/privacy-policy">Privacy Policy</Link>.
        </p>
      </section>

      <section>
        <h2>6. User Conduct</h2>
        <p>
          You agree to use the Service only for lawful purposes. You shall not:
        </p>
        <ul>
          <li>Attempt to gain unauthorized access to any part of the Service or its related systems</li>
          <li>Use the Service to distribute malware, spam, or other harmful content</li>
          <li>Reverse engineer, decompile, or otherwise attempt to extract the source code of the Service</li>
          <li>Impersonate any person or entity, including our staff or representatives</li>
          <li>Use the Service in any way that violates applicable local, national, or international law</li>
        </ul>
      </section>

      <section>
        <h2>7. Your Content</h2>
        <p>
          Any Icing Plans or data you save to the Service remain your own. We do not claim ownership
          of your saved plans. You grant us a limited license to store and display your content solely
          for the purpose of providing the Service to you. You may delete your saved plans at any time
          from within the Pro Calculator.
        </p>
      </section>

      <section>
        <h2>8. Intellectual Property</h2>
        <p>
          The Service and its original content, features, branding, and functionality are and will
          remain the exclusive property of The Icing Calculator and its licensors. Our trademarks,
          logos, and trade dress may not be used in connection with any product or service without
          our prior written consent.
        </p>
      </section>

      <section>
        <h2>9. Disclaimer of Warranties and Limitation of Liability</h2>
        <p>
          The calculations and tools provided by this Service are for informational and planning
          purposes only. Actual icing quantities may vary based on technique, consistency, and
          other factors. The Service is provided "AS IS" and "AS AVAILABLE" without any warranties
          of any kind, express or implied.
        </p>
        <p>
          In no event shall The Icing Calculator be liable for any indirect, incidental, special,
          consequential, or punitive damages arising from your use of or inability to use the Service,
          even if we have been advised of the possibility of such damages.
        </p>
      </section>

      <section>
        <h2>10. Governing Law</h2>
        <p>
          These Terms shall be governed and construed in accordance with the laws of the State of Texas,
          United States, without regard to its conflict of law provisions.
        </p>
      </section>

      <section>
        <h2>11. Changes to Terms</h2>
        <p>
          We reserve the right to modify these Terms at any time. If a revision is material, we will
          endeavor to provide reasonable notice before the new terms take effect. Continued use of the
          Service after changes are posted constitutes your acceptance of the revised Terms.
        </p>
      </section>

      <section>
        <h2>12. Contact Us</h2>
        <p>
          If you have any questions about these Terms, please contact us at:
          <br />
          Email: <a href="mailto:support@theicingcalculator.com">support@theicingcalculator.com</a>
        </p>
      </section>
      <div className="return-link-container return-link-bottom">
        <Link to="/" className="return-link">← Return to The Icing Calculator</Link>
      </div>
    </div>
  );
};

export default TermsOfServicePage;
